import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Globe, LayoutGrid, RefreshCw, Search, ShoppingCart, Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { RangeSelector } from '@/components/marketing/RangeSelector';
import { OverviewTab } from './dashboard/OverviewTab';
import { WebsiteTab } from './dashboard/WebsiteTab';
import { SearchTab } from './dashboard/SearchTab';
import { SocialTab } from './dashboard/SocialTab';
import { AmazonTab } from './dashboard/AmazonTab';
import {
  SOCIAL_PLATFORMS,
  type SocialPlatform,
} from '@/features/marketing/hooks';
import { MARKETING_RANGE_DAYS, type MarketingRange } from '@/features/marketing/range';

/**
 * The Marketing Dashboard — the surface the marketing team reviews on a
 * two-week cadence.
 *
 * Tab, date range, and social sub-platform all live in the URL rather than in
 * component state, so a specific view is linkable. That matters for this page
 * in particular: "here's the Instagram slowdown I mentioned" needs to survive
 * being pasted into Slack ahead of the review.
 *
 * Every tab reads live data through the hooks in `features/marketing/`, which
 * in turn read the daily rollup tables the hourly Edge Function syncs
 * populate. Nothing on this page is sample data — a channel that hasn't been
 * connected says so explicitly instead of showing plausible-looking zeros.
 */

type TabId = 'overview' | 'website' | 'search' | 'social' | 'amazon';

const TABS: Array<{ id: TabId; label: string; icon: typeof Globe }> = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid },
  { id: 'website', label: 'Website', icon: Globe },
  { id: 'search', label: 'Search', icon: Search },
  { id: 'social', label: 'Social media', icon: Users },
  { id: 'amazon', label: 'Amazon', icon: ShoppingCart },
];

const TAB_IDS = TABS.map((t) => t.id);

export function MarketingDashboardPage() {
  const [params, setParams] = useSearchParams();
  const queryClient = useQueryClient();

  const tabParam = params.get('tab') as TabId | null;
  const activeTab: TabId = tabParam && TAB_IDS.includes(tabParam) ? tabParam : 'overview';

  const rangeParam = params.get('range') as MarketingRange | null;
  const range: MarketingRange =
    rangeParam && rangeParam in MARKETING_RANGE_DAYS ? rangeParam : '28d';

  const platformParam = params.get('platform');
  const platform: SocialPlatform | 'all' =
    platformParam && (SOCIAL_PLATFORMS as string[]).includes(platformParam)
      ? (platformParam as SocialPlatform)
      : 'all';

  // `replace` keeps the browser back button meaningful — flipping between tabs
  // during a review shouldn't build up a history stack the operator has to
  // click through to leave the page.
  const updateParam = useCallback(
    (key: string, value: string) => {
      const next = new URLSearchParams(params);
      next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['marketing'] });
    queryClient.invalidateQueries({ queryKey: ['web-analytics'] });
  }, [queryClient]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Marketing dashboard"
        actions={
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        }
      />

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={activeTab} onValueChange={(value) => updateParam('tab', value)}>
          <TabsList className="h-auto flex-wrap gap-1">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabsTrigger key={tab.id} value={tab.id}>
                  <Icon className="mr-1.5 h-4 w-4" />
                  {tab.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
          {/* Radix needs a content node per trigger; the panels below render
              outside the Tabs root so the range selector can sit inline with
              the tab strip on wide screens. */}
          {TABS.map((tab) => (
            <TabsContent key={tab.id} value={tab.id} className="hidden" />
          ))}
        </Tabs>

        <RangeSelector value={range} onChange={(next) => updateParam('range', next)} />
      </div>

      {activeTab === 'overview' && <OverviewTab range={range} />}
      {activeTab === 'website' && <WebsiteTab range={range} />}
      {activeTab === 'search' && <SearchTab range={range} />}
      {activeTab === 'social' && (
        <SocialTab
          range={range}
          platform={platform}
          onPlatformChange={(next) => updateParam('platform', next)}
        />
      )}
      {activeTab === 'amazon' && <AmazonTab range={range} />}
    </div>
  );
}
